/**
 * 작성자 : 김광민
 * 날짜 : 2024.03.05
 * 기능 : 접고 펼치는 기능이 있고 길이 제한이 있는 텍스트 입력 컴포넌트
 */

import { Dispatch, SetStateAction, useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
// eslint-disable-next-line import/no-extraneous-dependencies
import TextareaAutosize from 'react-textarea-autosize';

const Root = styled.div`
  width: 100%;
  border-radius: 0.25rem;
  .title-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 2.75rem;
    border: 1px solid var(--outline);
    border-bottom: none;
    padding: 0 0.5rem;
    border-radius: 0.25rem 0.25rem 0 0;
    background-color: var(--board);

    .title {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      .material-symbols-rounded {
        cursor: pointer;
        color: var(--text-disabled);
        background-color: var(--tonal);
        padding: 0.125rem;
        border-radius: 0.25rem;
        &:hover {
          box-shadow: 0 0.25rem 0.75rem 0 rgba(0, 0, 0, 0.2);
          color: var(--text-tertiary);
        }
        &:active {
          background-color: var(--tonal-deep);
        }
      }
      .label {
        font-size: 0.875rem;
        color: var(--text-tertiary);
      }
    }
    .count {
      background-color: var(--tonal-deep-green);
      background-color: var(--tonal-green);
      border-radius: 0.25rem;
      font-size: 0.875rem;
      padding: 0 0.5rem;
      .value {
        color: var(--filled-green);
      }
      .max-length {
        color: var(--filled-green-deep);
      }
    }
  }
  textarea {
    border-radius: 0 0 0.25rem 0.25rem;
    border: 1px solid var(--outline-em);
    border-collapse: collapse;
    width: 100%;
    padding: 1rem;
    letter-spacing: normal;
    font-size: 0.938rem;
    line-height: 1.6;
    font-weight: 400;
    color: var(--text-secondary);
    resize: none;
    min-height: 8rem;
    max-height: 24rem;
    background-color: var(--board);
    &:focus {
      box-shadow: 0 0.25rem 0.75rem 0 rgba(0, 0, 0, 0.15);
    }
  }
  &.collapsed {
    .title-bar {
      border: 1px solid var(--outline);
      border-radius: 0.25rem;
      &:hover {
        box-shadow: 0 0.25rem 0.75rem 0 rgba(0, 0, 0, 0.1);
      }
    }
    textarea {
      display: none;
    }
  }
`;

const DropdownList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: var(--board);
  border: 1px solid var(--outline);
  border-top: none;
  border-radius: 0 0 0.25rem 0.25rem;
  box-shadow: 0 0.25rem 0.75rem 0 rgba(0, 0, 0, 0.15);
  max-height: 12rem;
  overflow-y: auto;
  z-index: 1000;

  .dropdown-item {
    padding: 0.75rem 1rem;
    cursor: pointer;
    font-size: 0.875rem;
    color: var(--text-secondary);
    border-bottom: 1px solid var(--outline-light);

    &:hover {
      background-color: var(--tonal);
    }

    &:last-child {
      border-bottom: none;
    }
  }
`;

const TextAreaContainer = styled.div`
  position: relative;
  width: 100%;
`;

type Props = {
  name: string;
  label: string;
  state: any;
  setState: Dispatch<SetStateAction<any>>;
  maxLength?: number;
  isExpandedState?: boolean;
  suggestionList?: string[]; // 추가: 미리 정의된 문자열 배열
};

const AccordionTextArea = ({
  name,
  label,
  state,
  setState,
  maxLength,
  isExpandedState = false,
  suggestionList = [], // 기본값으로 빈 배열
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(isExpandedState);
  const [showDropdown, setShowDropdown] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const onChangeValue = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value, name: targetName } = e.target;
    // 최대 길이 제한 처리
    const newValue = maxLength !== undefined ? value.slice(0, maxLength) : value;
    // 스프레드 연산자와 함께 상태 업데이트
    setState((prev: any) => ({ ...prev, [targetName]: newValue }));
  };

  const handleFocus = () => {
    if (suggestionList.length > 0) {
      setShowDropdown(true);
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // 드롭다운 아이템 클릭 시 blur 이벤트가 발생하므로 약간의 지연을 줌
    setTimeout(() => {
      if (!dropdownRef.current?.contains(e.relatedTarget as Node)) {
        setShowDropdown(false);
      }
    }, 150);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setState((prev: any) => ({ ...prev, [name]: suggestion }));
    setShowDropdown(false);
  };

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && textareaRef.current && !textareaRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getStyle = () => {
    const expandedState = isExpanded ? 'expanded' : 'collapsed';
    return `${expandedState}`;
  };

  return (
    <Root className={getStyle()}>
      <div className='title-bar'>
        <div className='title'>
          <span className='material-symbols-rounded' onClick={() => setIsExpanded(!isExpanded)} role='button' tabIndex={0}>
            {!isExpanded ? 'chevron_right' : 'expand_more'}
          </span>
          <span className='label'>{label}</span>
        </div>
        <div className='count'>
          <span className='value'>{state[name]?.length || 0}</span>
          <span className='max-length'> / {maxLength}</span>
        </div>
      </div>

      <TextAreaContainer>
        <TextareaAutosize ref={textareaRef} maxLength={maxLength || 300} rows={5} name={name} onChange={onChangeValue} value={state[name] || ''} onFocus={handleFocus} onBlur={handleBlur} />

        {showDropdown && suggestionList.length > 0 && (
          <DropdownList ref={dropdownRef}>
            {suggestionList.map((suggestion, index) => (
              <div key={index} role='button' tabIndex={0} className='dropdown-item' onClick={() => handleSuggestionClick(suggestion)}>
                {suggestion}
              </div>
            ))}
          </DropdownList>
        )}
      </TextAreaContainer>
    </Root>
  );
};

export default AccordionTextArea;
